/**
 * US phone-number formatting for the lead form's phone inputs (WWW-S035).
 *
 * Deliberately a byte-for-byte port of the same helper in the broker portal
 * (`src/lib/phone.ts`) and the PMS admin portal (`lib/phone.ts`), so a phone
 * number reads identically wherever a Signal user sees one. If the rule changes,
 * change it in all three.
 *
 * Strips everything but digits, caps at the 10 of a US number, and formats
 * progressively as the user types: "(XXX) XXX-XXXX". Partial input formats
 * partially so the mask grows under the cursor rather than jumping only when
 * complete.
 */
export function formatUsPhone(value: string): string {
  const digits = value.replace(/\D/g, "").slice(0, 10);
  const len = digits.length;
  if (len === 0) return "";
  if (len < 4) return `(${digits}`;
  if (len < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/**
 * Apply the mask live to every `[data-phone]` input in a container.
 *
 * The formatted string is what gets posted and what lands in Salesforce — those
 * are text `Phone` fields, and a human reading the Lead should see the same
 * shape they see in the portals. Reformatting on each keystroke is safe because
 * the mask is idempotent: formatting an already-formatted value is a no-op.
 */
export function initPhoneInputs(root: ParentNode): void {
  for (const input of root.querySelectorAll<HTMLInputElement>("[data-phone]")) {
    input.addEventListener("input", () => {
      const before = input.value;
      const formatted = formatUsPhone(before);
      if (formatted === before) return;
      // Typing at the end is the overwhelming case, so keep the caret pinned to
      // the end rather than trying to map an index across an inserted "(" or "-".
      const atEnd = input.selectionStart === before.length;
      input.value = formatted;
      if (atEnd) input.setSelectionRange(formatted.length, formatted.length);
    });
  }
}
